/**
 * ============================================================================
 * [P3-03-D] ScriptList - 스크립트 목록 컴포넌트
 *
 * 기능:
 * - 스크립트 목록 표시 (부모에서 prop으로 주입)
 * - 로딩/빈 상태 처리
 * - 스크립트 선택 이벤트 전달
 * - [DP-01] 듀얼 패널 선택 상태 표시
 *
 * [2026-01-28] Firestore 연동 완료
 * [2026-03-02] 듀얼 패널 고정 기능 추가
 * [2026-08-15] [SC-02] 죽은 구독 제거 - useScripts() 대신 prop 주입
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { ScriptCard } from './ScriptCard'
import type { Script, PanelIndex } from '@/types'
import styles from './Management.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface ScriptListProps {
  onSelect: (script: Script) => void
  panel1Id?: string       // [DP-01] 패널 1에 선택된 스크립트 ID
  panel2Id?: string       // [DP-01] 패널 2에 선택된 스크립트 ID
  // [P10-02] 필터링된 스크립트 목록을 prop으로 받을 수 있도록 추가
  scripts?: Script[]
  // [SC-02] 로딩/에러 상태도 부모(useScripts 보유자)에게서 주입받음
  isLoading?: boolean
  error?: Error | null
}

// ============================================================================
// 3. ScriptList Component
// ============================================================================
export function ScriptList({ onSelect, panel1Id, panel2Id, scripts: scriptsProp, isLoading, error }: ScriptListProps) {
  // ========================================================================
  // 3-1. [SC-02] prop 주입 데이터 (자체 useScripts 구독 제거)
  // ========================================================================
  const scripts = scriptsProp ?? []

  // ========================================================================
  // 3-2. [DP-01] 패널 선택 상태 계산
  // ========================================================================
  const getPanelSelection = (scriptId: string): PanelIndex | null => {
    if (scriptId === panel1Id) return 0
    if (scriptId === panel2Id) return 1
    return null
  }

  // ========================================================================
  // 3-3. 로딩 상태 렌더링
  // ========================================================================
  if (isLoading) {
    return <div className={styles.loading}>로딩 중...</div>
  }

  // ========================================================================
  // 3-4. 에러 상태 렌더링
  // ========================================================================
  if (error) {
    return (
      <div className={styles.errorState}>
        <p>스크립트를 불러오는데 실패했습니다.</p>
        <p className={styles.errorDetail}>{error.message}</p>
      </div>
    )
  }

  // ========================================================================
  // 3-5. 빈 목록 상태 렌더링
  // ========================================================================
  if (scripts.length === 0) {
    return (
      <div className={styles.emptyList}>
        <p>저장된 스크립트가 없습니다.</p>
        <p className={styles.emptyHint}>위의 "+ 새 스크립트" 버튼을 눌러 만들어보세요.</p>
      </div>
    )
  }

  // ========================================================================
  // 3-6. 스크립트 목록 렌더링
  // ========================================================================
  return (
    <div className={styles.scriptList}>
      {scripts.map((script) => (
        <ScriptCard
          key={script.id}
          script={script}
          panelSelection={getPanelSelection(script.id)}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

export default ScriptList
